/* @flow weak */
import React from 'react';
import ReactDOM from 'react-dom';

const LayeredComponentMixin = {
  propTypes: {
    container(props, propName, componentName) {
      if (!(propName in props) || props[propName] === null) {
        return;
      }
      if (typeof props[propName] !== 'object' ||
          typeof props[propName].render !== 'function' && props[propName].nodeType !== 1) {
        throw new Error(
          'Invalid prop `' + propName + '` supplied to `' + componentName + '`, expected DOM ' +
          'node or an object that has a `render` method'
        );
      }
    },
    defaultLayerShown: React.PropTypes.bool,
    onShow: React.PropTypes.func,
    onHide: React.PropTypes.func,
  },

  getInitialState() {
    return {
      layerShown: this.props.defaultLayerShown,
    };
  },

  isLayerShown() {
    return this.state.layerShown;
  },

  showLayer() {
    if (!this.isMounted()) {
      console.warn('LayeredComponentMixin showLayer() while unmounted');
      return;
    }
    this.setState({ layerShown: true });
    if (this.props.onShow) {
      this.props.onShow();
    }
  },

  hideLayer() {
    if (!this.isMounted()) {
      console.warn('LayeredComponentMixin hideLayer() while unmounted');
      return;
    }
    this.setState({ layerShown: false });
    if (this.props.onHide) {
      this.props.onHide();
    }
  },

  toggleLayer() {
    if (this.state.layerShown) {
      this.hideLayer();
    } else {
      this.showLayer();
    }
  },

  componentWillUpdate(nextProps, nextState) {
    this._shouldRenderLayer = (
      typeof this.shouldLayeredComponentUpdate !== 'function' ||
      this.shouldLayeredComponentUpdate(nextProps, nextState)
    );
  },

  componentDidUpdate() {
    if (this._shouldRenderLayer) {
      this._renderLayer();
    }
  },

  componentDidMount() {
    if (this.state.layerShown) {
      this._renderLayer();
    }
  },

  componentWillUnmount() {
    this._unrenderLayer();
    this._unmountLayerTarget();
  },

  _mountLayerTarget() {
    if (!this._layerTarget) {
      this._layerTarget = document.createElement('div');
      this.getContainerDOMNode().appendChild(this._layerTarget);
    }
  },

  _unmountLayerTarget() {
    if (this._layerTarget) {
      if (this._layerTarget.parentNode) {
        this._layerTarget.parentNode.removeChild(this._layerTarget);
      }
      this._layerTarget = null;
    }
  },

  _renderLayer() {
    const layer = this.state.layerShown ? this.renderLayer() : null;
    if (layer) {
      this._mountLayerTarget();
      // XXX undocumented API, though present in latest React, 0.15
      this._layerInstance = ReactDOM.unstable_renderSubtreeIntoContainer(
        this, layer, this._layerTarget);
    } else {
      this._unrenderLayer();
      this._unmountLayerTarget();
    }
  },

  _unrenderLayer() {
    if (this._layerTarget) {
      ReactDOM.unmountComponentAtNode(this._layerTarget);
      this._layerInstance = null;
    }
  },

  getLayerDOMNode() {
    if (!this.isMounted()) {
      throw new Error('getLayerDOMNode(): A component must be mounted to have a DOM node.');
    }

    if (this._layerInstance) {
      return ReactDOM.findDOMNode(this._layerInstance);
    }

    return null;
  },

  getContainerDOMNode() {
    const container = ReactDOM.findDOMNode(this.props.container);
    if (container) {
      return container;
    }

    const element = ReactDOM.findDOMNode(this);
    return ((element && element.ownerDocument) || document).body;
  },
};

export default LayeredComponentMixin;



// WEBPACK FOOTER //
// ./src/components/mixins/LayeredComponentMixin.js